/* ============================================================================
 * milliData.js — talk to Milli (K.O. Command) from the dashboard.
 *
 * Chat goes through the `milli-chat` Edge Function, which runs the same
 * audited orchestrator loop as the PC service: every turn opens a
 * kc_agent_runs row, tools are the kc_tool_* RPCs, conversation memory is
 * kc_conversations. Auth is the caller's Supabase session — the function
 * verifies _kc_is_owner() server-side, so only K.O. Command owner accounts
 * get anything but a 403.
 *
 * Status reads are plain selects; the owner-only RLS from migration 044
 * makes them return zero rows for anyone else, so there's nothing to hide
 * client-side.
 * ========================================================================== */

import { supabase } from '@/lib/supabaseClient';

const SESSION_KEY = 'milli_session_id';

/** The conversation id this browser last used — a fallback only; see
 * resolveSessionId, which prefers the owner's latest thread server-side so
 * every device shows the same conversation. Server-side history is capped
 * to the same ~40-message window the PC orchestrator uses. */
export const getStoredSessionId = () => {
  try {
    return localStorage.getItem(SESSION_KEY) || null;
  } catch {
    return null;
  }
};

export const storeSessionId = (id) => {
  try {
    if (id) localStorage.setItem(SESSION_KEY, id);
  } catch {
    /* private mode etc. — continuity degrades, chat still works */
  }
};

export const clearStoredSessionId = () => {
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch {
    /* ignore */
  }
};

/**
 * Which conversation should this device show? The owner's most recently
 * active one — wherever it happened. A question asked on the laptop is the
 * thread the phone opens to a minute later; the per-browser id is only the
 * fallback when the server can't be asked (offline, private mode).
 *
 * kc_conversations is owner-only under RLS, so for anyone else the query
 * returns no rows and this degrades to the stored id exactly as before.
 */
export const resolveSessionId = async () => {
  const stored = getStoredSessionId();
  try {
    const { data, error } = await supabase
      .from('kc_conversations')
      .select('session_id')
      .is('archived_at', null)
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (!error && data?.session_id) {
      storeSessionId(data.session_id);
      return data.session_id;
    }
  } catch {
    /* unreachable — keep whatever this browser last used */
  }
  return stored;
};

/**
 * Rooms (migration 066). A room is a kc_conversations row with a title, an
 * optional business tag, and an archive flag. Every call runs as the
 * signed-in owner; the RPCs refuse anyone else. Memory is shared across
 * rooms on purpose — only the transcript is per room.
 */
export const BUSINESSES = ['K.O. Construction & Remodeling', 'K.O. CO+RE Systems', 'PQ Intelligence', 'FormQuick.io'];
export const BUSINESS_SHORT = {
  'K.O. Construction & Remodeling': 'Construction',
  'K.O. CO+RE Systems': 'CO+RE',
  'PQ Intelligence': 'PQ',
  'FormQuick.io': 'FormQuick',
};

const rpc = async (name, params) => {
  const { data, error } = await supabase.rpc(name, params);
  if (error) throw new Error(error.message);
  return data;
};

export const listConversations = async (includeArchived = false) =>
  (await rpc('kc_conversation_list', { p_include_archived: includeArchived })) ?? [];

export const createConversation = async (title, business) => {
  const id = await rpc('kc_conversation_create', { p_title: title || null, p_business: business || null });
  storeSessionId(id);
  return id;
};

export const renameConversation = (sessionId, title) =>
  rpc('kc_conversation_rename', { p_session_id: sessionId, p_title: title });

export const setConversationBusiness = (sessionId, business) =>
  rpc('kc_conversation_set_business', { p_session_id: sessionId, p_business: business || null });

export const archiveConversation = (sessionId, archived = true) =>
  rpc('kc_conversation_archive', { p_session_id: sessionId, p_archived: archived });

// "I'm looking at this one now" — makes it the room every device opens to.
export const touchConversation = async (sessionId) => {
  try {
    await rpc('kc_conversation_touch', { p_session_id: sessionId });
  } catch {
    /* cosmetic; the switch itself still happened locally */
  }
};

export const roomLabel = (room, untitled = 'Untitled') => {
  const name = room?.title || room?.preview || untitled;
  const biz = room?.business ? BUSINESS_SHORT[room.business] ?? room.business : null;
  return biz ? `${biz} · ${name}` : name;
};

/**
 * Send one message. Returns { reply, run_id, session_id }.
 * Throws with the server's real error message on failure.
 */
export const sendMilliMessage = async (message, sessionId, attachments = []) => {
  const { data, error } = await supabase.functions.invoke('milli-chat', {
    body: {
      message,
      ...(sessionId ? { session_id: sessionId } : {}),
      ...(attachments.length ? { attachments } : {}),
    },
  });
  if (error) {
    // supabase-js buries the response body; surface the function's actual
    // error text when we can get at it.
    let detail = error.message || 'Milli is unreachable.';
    try {
      const ctx = await error.context?.json?.();
      if (ctx?.error) detail = ctx.error;
    } catch {
      /* keep the generic message */
    }
    throw new Error(detail);
  }
  if (data?.error) throw new Error(data.error);
  return data;
};

/**
 * Rehydrate the visible transcript from the server-side conversation store
 * (kc_conversations — owner-only RLS, so this read returns nothing for
 * anyone else). The stored shape is raw Anthropic API messages, which
 * include tool_use / tool_result plumbing; this flattens them to just the
 * turns a person actually said or read:
 *   - user turns with string content        -> user bubbles
 *   - assistant turns' text blocks, joined  -> milli bubbles
 *   - tool_use / tool_result blocks         -> skipped (visible in the run
 *     activity panel, not the transcript)
 */
export const loadConversationHistory = async (sessionId) => {
  if (!sessionId) return [];
  const { data, error } = await supabase
    .from('kc_conversations')
    .select('messages')
    .eq('session_id', sessionId)
    .maybeSingle();
  if (error || !data?.messages) return [];

  const turns = [];
  for (const msg of data.messages) {
    if (msg?.role === 'user' && typeof msg.content === 'string') {
      turns.push({ role: 'user', text: msg.content });
    } else if (msg?.role === 'user' && Array.isArray(msg.content)) {
      // Attachment turns store text blocks (the typed message + a small
      // placeholder where each file was); tool_result arrays yield nothing.
      const text = msg.content
        .filter((b) => b?.type === 'text' && b.text)
        .map((b) => b.text)
        .join('\n');
      if (text.trim()) turns.push({ role: 'user', text });
    } else if (msg?.role === 'assistant' && Array.isArray(msg.content)) {
      const text = msg.content
        .filter((b) => b?.type === 'text' && b.text)
        .map((b) => b.text)
        .join('');
      if (text.trim()) turns.push({ role: 'milli', text });
    }
  }
  return turns;
};

/**
 * Live status for the cockpit side panels. Each piece degrades to empty
 * independently — a failed panel must not blank the chat.
 */
export const fetchMilliStatus = async () => {
  const [runs, tasks, approvals] = await Promise.all([
    supabase
      .from('kc_agent_runs')
      .select('id,status,trigger_source,started_at,ended_at,cost_usd')
      .order('started_at', { ascending: false })
      .limit(8),
    supabase
      .from('kc_tasks')
      .select('id,title,business,status,due_date')
      .in('status', ['open', 'in_progress', 'blocked'])
      .order('created_at', { ascending: false })
      .limit(8),
    supabase
      .from('kc_approvals')
      .select('id', { count: 'exact', head: true })
      .eq('status', 'pending'),
  ]);

  return {
    runs: runs.data || [],
    tasks: tasks.data || [],
    pendingApprovals: approvals.count || 0,
  };
};

/**
 * The run ledger for the console: recent kc_agent_runs with the plan each
 * was filed under (plan.department / secondary / routed_by since milli-chat
 * v50) and its real cost. Owner-only RLS; anyone else gets an empty list.
 */
export const fetchRunLedger = async (limit = 30) => {
  const { data, error } = await supabase
    .from('kc_agent_runs')
    .select('id,status,trigger_source,plan,cost_usd,started_at,ended_at')
    .order('started_at', { ascending: false })
    .limit(limit);
  if (error) throw new Error(error.message);
  return data || [];
};

/** Today's runs and spend, from a ledger list (local midnight). */
/**
 * History (migration 077): everything Milli did, one timeline. Every row
 * has the same shape { at, kind, id, title, subtitle, status, business,
 * department, cost_usd, ref }; the server merges the sixteen sources,
 * filters, searches and pages, and returns { total, rows }. Oldest first
 * unless newestFirst is set. Owner-only: the RPC refuses anyone else.
 */
export const HISTORY_KINDS = [
  'chat', 'run', 'room', 'approval', 'email', 'post', 'graphic', 'flyer',
  'video', 'blog', 'content', 'task', 'decision', 'memory', 'research', 'asset', 'file', 'call',
];

export const fetchHistory = async ({
  before = null, after = null, kinds = [], department = '', business = '', q = '',
  limit = 50, offset = 0, newestFirst = false,
} = {}) => {
  const data = await rpc('kc_history', {
    p_before: before || null,
    p_after: after || null,
    p_kinds: kinds?.length ? kinds : null,
    p_department: department || null,
    p_business: business || null,
    p_q: q || null,
    p_limit: limit,
    p_offset: offset,
    p_newest_first: !!newestFirst,
  });
  return { total: Number(data?.total) || 0, rows: Array.isArray(data?.rows) ? data.rows : [] };
};

/** One run opened: { run, tool_calls, children } or null when it does not exist. */
export const fetchRunDetail = (runId) => rpc('kc_run_detail', { p_run_id: runId });

/** A one-hour signed URL for a voicemail recording (private bucket, owner policy, 103). */
export const signVoicemailPath = async (path, ttlSeconds = 60 * 60) => {
  if (!path) return null;
  const { data, error } = await supabase.storage.from('kc-voicemail').createSignedUrl(path, ttlSeconds);
  if (error) return null;
  return data?.signedUrl || null;
};

export const summarizeRunsToday = (runs = [], now = new Date()) => {
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  let count = 0;
  let cost = 0;
  for (const r of runs) {
    const t = new Date(r.started_at).getTime();
    if (Number.isNaN(t) || t < start) continue;
    count += 1;
    cost += Number(r.cost_usd) || 0;
  }
  return { count, cost };
};

/* ----------------------------------------------------------------------------
 * Attachments — photos and PDFs handed to Milli in chat.
 *
 * Job-site phone photos are 10MB+ of pixels she doesn't need, so images get
 * downscaled client-side (longest edge 1600px, JPEG) before upload — faster
 * on truck data, cheaper per turn, still plenty to read a letter. PDFs pass
 * through untouched under a 5MB cap. The server enforces its own limits
 * regardless (4 files, ~5MB each, photos/PDFs only).
 * ------------------------------------------------------------------------- */

/* Deliberately broad: Samsung's picker has returned empty selections
 * against a long, specific accept list (even for files saved on the
 * phone), and wildcard accepts route through Android's standard media
 * picker path, which grants file access far more reliably. What actually
 * gets attached is validated in fileToAttachment() regardless. */
export const ATTACHMENT_ACCEPT = 'image/*,application/pdf,.pdf';
export const MAX_ATTACHMENTS = 4;
const MAX_FILE_BYTES = 5 * 1024 * 1024;
const MAX_IMAGE_EDGE = 1600;

/* Phone pickers are sloppy about MIME types: iPhone photos arrive as
 * image/heic (which the API can't take), and some Android file managers
 * hand over files with an empty type string entirely. Fall back to the
 * filename extension so a real photo or PDF never bounces for paperwork
 * reasons. HEIC gets force-converted to JPEG through the canvas (Safari
 * decodes HEIC natively; that's the browser where HEIC shows up). */
const EXT_TYPES = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  gif: 'image/gif',
  heic: 'image/heic',
  heif: 'image/heif',
  pdf: 'application/pdf',
};

const effectiveType = (file) => {
  if (file.type) return file.type.toLowerCase();
  const ext = (file.name || '').split('.').pop().toLowerCase();
  return EXT_TYPES[ext] || '';
};

const readAsDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error('Could not read file'));
    r.readAsDataURL(blob);
  });

const downscaleImage = (file, forceJpeg = false) =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, MAX_IMAGE_EDGE / Math.max(img.width, img.height));
      if (!forceJpeg && scale === 1 && file.size < 1.5 * 1024 * 1024) {
        resolve(null); // small enough — send as-is
        return;
      }
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.85);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });

/** Convert a File into the {media_type, data, name} shape milli-chat takes.
 * Throws with a human message for unsupported/oversized files. */
export const fileToAttachment = async (file) => {
  // Android hands over a 0-byte placeholder for a file that only lives in
  // Drive or email; that is the real "not on this phone" case.
  if (!file.size) {
    throw new Error(
      `${file.name}: is empty on this phone. If it lives in Drive or email, download it first, then attach it from Files.`
    );
  }
  const type = effectiveType(file);
  const isPdf = type === 'application/pdf';
  const isHeic = /^image\/hei[cf]$/.test(type);
  const isImage = isHeic || /^image\/(jpeg|png|webp|gif)$/.test(type);
  if (!isPdf && !isImage) {
    throw new Error(`${file.name}: photos and PDFs only`);
  }

  let blob = file;
  let mediaType = type;
  if (isImage) {
    const scaled = await downscaleImage(file, isHeic);
    if (scaled) {
      blob = scaled;
      mediaType = 'image/jpeg';
    } else if (isHeic) {
      // The browser couldn't decode this HEIC (non-Safari, usually).
      throw new Error(
        `${file.name}: this phone photo format (HEIC) couldn't be converted here. ` +
          `Share it as a JPEG, or set iPhone Camera > Formats to Most Compatible.`
      );
    }
  }
  if (blob.size > MAX_FILE_BYTES) {
    throw new Error(`${file.name}: too large (max 5MB)`);
  }

  const dataUrl = await readAsDataUrl(blob);
  const data = String(dataUrl).split(',')[1] || '';
  if (!data) throw new Error(`${file.name}: could not encode`);
  return { media_type: mediaType, data, name: file.name };
};

/**
 * Process a picker's FileList into attachments. Never silent: every file
 * yields either an attachment or an error line, an empty selection gets
 * its own explanation (Samsung's Files app hands back nothing when the
 * pick is a cloud-only file that isn't on the phone), and a read that
 * hangs gets cut off with a message instead of spinning forever.
 */
export const filesToAttachments = async (fileList, room = MAX_ATTACHMENTS) => {
  const files = Array.from(fileList || []);
  const attachments = [];
  const errors = [];
  if (!files.length) {
    // Rare now that callers copy the list before resetting the input
    // (Chromium empties the live FileList on reset; see useMilliChat).
    errors.push('No file came back from the picker. Try again, or download it to this phone first and pick it from Files.');
    return { attachments, errors };
  }
  for (const f of files.slice(0, Math.max(0, room))) {
    try {
      const att = await Promise.race([
        fileToAttachment(f),
        new Promise((_, reject) =>
          setTimeout(
            () =>
              reject(
                new Error(
                  `${f.name}: couldn't be read after 20 seconds. Make sure ` +
                    "it's downloaded onto this phone, not just in the cloud."
                )
              ),
            20000
          )
        ),
      ]);
      attachments.push(att);
    } catch (err) {
      errors.push(err?.message || `${f.name}: could not be read`);
    }
  }
  if (files.length > room) {
    errors.push(`Only ${MAX_ATTACHMENTS} files per message. The extras were dropped.`);
  }
  return { attachments, errors };
};
